import { useState } from 'react';
import { When } from 'react-if';
import { toast } from 'react-toastify';
import { Tooltip } from '@mui/material';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { CallRecordingAction } from 'shared';

import { Button } from '../../../../../common/Atoms/Button';
import { classNames } from '../../../../../common/lib/classNames';
import { useControlCallRecordingMutation } from '../../../../services/api/contactsApi/contact';

type RecordingState = `off` | `on` | `declined` | `dismissed`;

/** Seconds between nudges to record or dismiss while the call is unrecorded */
const NUDGE_EVERY_SECONDS = 10;

interface ICallRecordingProps {
  organisationId: string;
  contactId: number;
  callId: string;
  /** The org auto transcribes calls, so it has been recording since the contact answered */
  autoTranscribing: boolean;
  /** Seconds since the call connected, ticked by the status modal */
  elapsed: number;
}

/**
 * Transcribe on demand. Nothing is recorded until the user clicks Start Transcribing, unless the org auto transcribes
 * calls. It records until the call ends unless they stop and remove it. Not transcribing, it nudges every 10 seconds
 * until they start or choose not to. Key it by call id so each call starts fresh
 */
export function CallRecording({ organisationId, contactId, callId, autoTranscribing, elapsed }: ICallRecordingProps) {
  const [state, setState] = useState<RecordingState>(autoTranscribing ? `on` : `off`);
  const [control, { isLoading }] = useControlCallRecordingMutation();
  const nudging = state === `off` && elapsed >= NUDGE_EVERY_SECONDS;
  // A two second flash at each 10 second mark
  const flash = nudging && elapsed % NUDGE_EVERY_SECONDS < 2;

  async function act(action: CallRecordingAction, next: RecordingState) {
    try {
      await control({ organisationId, contactId, callId, action }).unwrap();
      setState(next);
    }
    catch (err) {
      toast.error((err as { data?: { message?: string } })?.data?.message || `Could not update the transcription`);
    }
  }

  if (state === `on`) {
    return (
      <div className={ `mt-4 flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3` }>
        <span className={ `relative flex h-2.5 w-2.5` }>
          <span className={ `absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75` } />
          <span className={ `relative inline-flex h-2.5 w-2.5 rounded-full bg-blue-600` } />
        </span>
        <span className={ `text-sm font-medium text-blue-900` }>{ `Transcribing this call` }</span>
        { /* The span keeps the tooltip working while the button is disabled */ }
        <Tooltip title={ `Stop & Remove transcription. The recording is deleted` }>
          <span className={ `ml-auto` }>
            <button
              type={ `button` }
              disabled={ isLoading }
              onClick={ () => act(`decline`, `declined`) }
              aria-label={ `Stop & Remove transcription` }
              className={ `flex h-7 w-7 cursor-pointer items-center justify-center rounded-full text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50` }
            >
              <XMarkIcon className={ `h-5 w-5` } />
            </button>
          </span>
        </Tooltip>
      </div>
    );
  }

  if (state !== `off`) {
    return (
      <p className={ `mt-4 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-600` }>
        { {
          declined: `Transcription stopped. The recording will be deleted.`,
          dismissed: `Not transcribing this call.`,
        }[state] }
      </p>
    );
  }

  return (
    <div className={ classNames(
      `mt-4 flex items-center gap-3 rounded-lg border p-3 transition-shadow`,
      nudging ? `border-amber-300 bg-amber-50` : `border-gray-200 bg-white`,
      flash && `ring-2 ring-amber-400`,
    ) }>
      <div className={ `min-w-0` }>
        <p className={ `text-sm font-medium text-gray-900` }>{ nudging ? `Transcribe this call?` : `Not transcribing` }</p>
        <p className={ `text-xs text-gray-600` }>{ `Records the call from now on so you get a transcript and summary.` }</p>
      </div>
      <div className={ `ml-auto flex shrink-0 items-center gap-3` }>
        <When condition={ nudging }>
          <button
            type={ `button` }
            disabled={ isLoading }
            onClick={ () => act(`dismiss`, `dismissed`) }
            className={ `cursor-pointer text-xs text-gray-600 underline hover:text-gray-900 disabled:cursor-not-allowed` }
          >
            { `Don't transcribe this call` }
          </button>
        </When>
        <Button
          color={ `paidniceGreen` }
          size={ `md` }
          disabled={ isLoading }
          onClick={ () => act(`start`, `on`) }
        >
          { `Start Transcribing` }
        </Button>
      </div>
    </div>
  );
}
